import 'react-loading-skeleton/dist/skeleton.css';

import { Suspense, useEffect, useRef, useState } from 'react';
import { useDelaySwitch } from '@dsg-tech/homefield-react';
import {
  aosSelector,
  apiEndpointsSelector,
  breakpointSelector,
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from 'store/selectors/commonSelector';
import { showPreSaytDrawerSelector, showSaytDrawerSelector } from 'store/selectors/saytSelector';

import { StoreIdentifierName } from 'types';
import {
  headerTaxoSelector,
  progressiveHeaderTaxoSelector,
} from 'store/selectors/taxonomySelector';
import { setProgressiveHeaderTaxo } from 'store/slices/taxonomy';
import { useAppSelector } from 'store/store';
import useHomr from 'hooks/useHomr';
import usePARTY from 'hooks/usePARTY';
import useSAYT from 'hooks/useSAYT';
import useTrendingSearches from 'hooks/useTrendingSearches';
import ErrorBoundaryWithLogger from 'common/ErrorBoundary/ErrorBoundary';
import AssociateOrderingSystem from './AOS/AOS';
import DesktopNav from './Navigation/DesktopNav/DesktopNav';
import ConsolidatedNav from './ConsolidatedNav/ConsolidatedNav';
import FooterSkipLink from './SkipLinks/FooterSkipLink';
import MainContentSkipLink from './SkipLinks/MainContentSkipLink';
import MainHeader from './MainHeader/MainHeader';
import MobileNav from './Navigation/MobileNav/MobileNav';
import Modal from '../../common/Modal/Modal';
import MyAccountDrawer from './MyAccountDrawer/MyAccountDrawer';
import ProgressiveHeader from './ProgressiveHeader/ProgressiveHeader';
import Ticker, { TickerFallback } from '../../common/Ticker/Ticker';
import TopNavBarContainer from './NavBar/TopNavBarContainer';
import './Header.scss';
import TopNavBarNoContent from './NavBar/TopNavBarNoContent';
import DesktopNavNoContent from './Navigation/DesktopNav/DesktopNavNoContent';
import TrendingSearchesContainer from './TrendingSearches/TrendingSearchesContainer';
import TrendingSearchesNoContent from './TrendingSearches/TrendingSearchesNoContent';

declare global {
  interface Window {
    authFunctions: any;
    ceopsStatus: any;
    _dsgTag: any;
  }
}

export default function Header() {
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const headerTaxoContent = useAppSelector(headerTaxoSelector);
  const progressiveHeaderTaxoContent = useAppSelector(progressiveHeaderTaxoSelector);
  const renderWithProgressiveTaxo = useAppSelector(
    getFeatureFlagByNameSelector('progressiveTaxoDesktop'),
  );
  // Determine which taxo data to use. We're currently attempting to deploy a switchover to using
  // the progressiveHeaderTaxo content. Once this has been validated and we're ready to roll it
  // out to all brands, we can delete this and set taxoContent to progressiveHeaderTaxoContent directly
  //
  // - Blair Wilcox 6/4/2025
  const taxoContent = renderWithProgressiveTaxo
    ? progressiveHeaderTaxoContent ?? {} // progressiveTaxo isn't set in initial state, so it can be undefined, while headerTaxo would be an empty object. This ensures both have similar types
    : headerTaxoContent;
  const apiObject = useAppSelector(apiEndpointsSelector);
  const aos = useAppSelector(aosSelector);
  const viewPortBreakpoint = useAppSelector(breakpointSelector);
  const headerRef = useRef(null);
  const [hasTopNavViewEventBeenCalled, setHasTopNavViewEventBeenCalled] = useState<boolean>(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const progressiveHeaderEnabled = useAppSelector(
    getFeatureFlagByNameSelector('progressiveHeader'),
  );
  const hideUtilityBar = useAppSelector(getFeatureFlagByNameSelector('hideUtilityBar'));
  const [nikeConnected] = usePARTY();
  const showSaytDrawer = useAppSelector(showSaytDrawerSelector);
  const showPreSaytDrawer = useAppSelector(showPreSaytDrawerSelector);
  useSAYT();
  const { storeByNumberEndpoint } = apiObject;
  useHomr(
    'progressive-header',
    'default',
    storeIdentifierName as StoreIdentifierName,
    setProgressiveHeaderTaxo,
  );
  const { state, neutral, switchState } = useDelaySwitch(false, 250);
  const {
    state: myAccountDrawerState,
    neutral: myAccountDrawerNeutral,
    switchState: myAccountDrawerSwitchState,
  } = useDelaySwitch(false, 250);
  const allCategoriesFlag = useAppSelector(getFeatureFlagByNameSelector('allCategories'));
  const l0LinksFlag = useAppSelector(getFeatureFlagByNameSelector('consolidatedNavL0Links'));
  const trendingSearchInConsolidatedFlag = useAppSelector(
    getFeatureFlagByNameSelector('trendingSearchInConsolidatedNav'),
  );
  const { trendingSearchData, showTrendingSearchData, isLoading } = useTrendingSearches();

  const isTickerDisabled = useAppSelector(getFeatureFlagByNameSelector('hideTicker'));

  // Analytics event: Fires only once per page load, when mobile or desktop header navigation is opened
  const viewTopNavAnalyticsEventHandler = (additionalData = {}) => {
    if (!hasTopNavViewEventBeenCalled) {
      const evt = new CustomEvent('TopNavView', {
        detail: additionalData,
      });
      document.dispatchEvent(evt);
      setHasTopNavViewEventBeenCalled(true);
    }
  };

  const mobileMenuToggleButtonEventHandler = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  useEffect(() => {
    window.headerFunctions = {
      ...(window.headerFunctions && { ...window.headerFunctions }),
      updateSelectedStore: () => {},
    };
  }, []);

  useEffect(() => {
    window.onpageshow = (event) => {
      if (event.persisted) {
        const evt = new CustomEvent('updateFavoritesStoreData', { detail: '' });
        window.dispatchEvent(evt);
      }
    };
  }, []);

  useEffect(() => {
    if (showMobileMenu && (showSaytDrawer || showPreSaytDrawer)) {
      setShowMobileMenu(false);
    }
  }, [showSaytDrawer, showPreSaytDrawer]);

  return (
    // Using a <div role="banner"> here instead of a normal header element because currently, in the
    // monorepo, there's a universal style of 10px padding being applied to every header element.
    // This seems like an overly-broad rule in the monorepo, and we should try to address that, but
    // for the time being, we're keeping this as a div
    //
    // Blair Wilcox, 8/12/2025
    <div role="banner" className="dsg-header-2021" data-testid="header">
      {aos ? (
        <ErrorBoundaryWithLogger componentId="Header-AOS">
          <AssociateOrderingSystem />
        </ErrorBoundaryWithLogger>
      ) : null}
      <ErrorBoundaryWithLogger componentId="Header-MainContentSkipLink">
        <MainContentSkipLink />
      </ErrorBoundaryWithLogger>
      <ErrorBoundaryWithLogger componentId="Header-FooterSkipLink">
        <FooterSkipLink />
      </ErrorBoundaryWithLogger>
      {!isTickerDisabled && (
        <ErrorBoundaryWithLogger componentId="Header-Ticker" fallback={<TickerFallback />}>
          <Suspense>
            <Ticker storeIdentifierName={storeIdentifierName as StoreIdentifierName} />
          </Suspense>
        </ErrorBoundaryWithLogger>
      )}
      {!hideUtilityBar && viewPortBreakpoint !== 'mobile' && aos === false && (
        <ErrorBoundaryWithLogger
          componentId="Header-TopNavBarContainer"
          fallback={<TopNavBarNoContent />}>
          <TopNavBarContainer />
        </ErrorBoundaryWithLogger>
      )}
      <ErrorBoundaryWithLogger componentId="Header-MainHeader">
        <MainHeader
          switchState={switchState}
          myAccountDrawerSwitchState={myAccountDrawerSwitchState}
          trendingSearchData={trendingSearchData}
          showMobileMenu={showMobileMenu}
          setShowMobileMenu={setShowMobileMenu}
          hasTopNavViewEventBeenCalled={hasTopNavViewEventBeenCalled}
          setHasTopNavViewEventBeenCalled={setHasTopNavViewEventBeenCalled}
          showTrendingSearch={showTrendingSearchData}
        />
      </ErrorBoundaryWithLogger>
      <ErrorBoundaryWithLogger componentId="Header-MobileNav">
        <MobileNav
          mobileNavData={taxoContent.Taxonomy2}
          navBarData={taxoContent.headerNavBar}
          showMobileMenu={showMobileMenu}
          setShowMobileMenu={setShowMobileMenu}
          storeByNumberEndpoint={storeByNumberEndpoint}
          storeIdentifierName={storeIdentifierName}
          showMobileMenuClickHandler={mobileMenuToggleButtonEventHandler}
          viewTopNavAnalyticsEvent={viewTopNavAnalyticsEventHandler}
          viewPortBreakpoint={viewPortBreakpoint}
          nikeConnected={nikeConnected}
        />
      </ErrorBoundaryWithLogger>
      {showTrendingSearchData && (
        <ErrorBoundaryWithLogger
          componentId="Header-TrendingSearchesContainer"
          fallback={showTrendingSearchData ? <TrendingSearchesNoContent /> : null}>
          <TrendingSearchesContainer
            trendingSearchData={trendingSearchData}
            showTrendingSearchData={showTrendingSearchData}
            isPersonalizationLoading={isLoading}
          />
        </ErrorBoundaryWithLogger>
      )}
      {(allCategoriesFlag || l0LinksFlag || trendingSearchInConsolidatedFlag) && (
        <ErrorBoundaryWithLogger componentId="Header-ConsolidatedNav">
          <ConsolidatedNav
            content={trendingSearchData}
            storeIdentifierName={storeIdentifierName}
            aos={aos}
            setShowDrawer={() => switchState()}
            navBarData={taxoContent.Taxonomy2}
            nikeConnected={nikeConnected}
            viewTopNavAnalyticsEventHandler={viewTopNavAnalyticsEventHandler}
          />
        </ErrorBoundaryWithLogger>
      )}
      {!allCategoriesFlag &&
        !l0LinksFlag &&
        !trendingSearchInConsolidatedFlag &&
        !progressiveHeaderEnabled && (
          <ErrorBoundaryWithLogger
            componentId="Header-DesktopNavContainer"
            fallback={<DesktopNavNoContent />}>
            <DesktopNav viewTopNavAnalyticsEvent={viewTopNavAnalyticsEventHandler} />
          </ErrorBoundaryWithLogger>
        )}
      <ErrorBoundaryWithLogger componentId="Header-Modal">
        <Modal />
      </ErrorBoundaryWithLogger>

      {state && (
        <ErrorBoundaryWithLogger componentId="Header-ProgressiveHeader">
          <ProgressiveHeader
            headerRef={headerRef.current}
            showDrawer={state}
            setShowDrawer={() => switchState()}
            closing={neutral}
            storeIdentifierName={storeIdentifierName}
            navBarData={progressiveHeaderTaxoContent}
          />
        </ErrorBoundaryWithLogger>
      )}
      <ErrorBoundaryWithLogger componentId="Header-MyAccountDrawer">
        <MyAccountDrawer
          headerRef={headerRef.current}
          showDrawer={myAccountDrawerState}
          setShowDrawer={() => myAccountDrawerSwitchState()}
          closing={myAccountDrawerNeutral}
          viewPortBreakpoint={viewPortBreakpoint}
        />
      </ErrorBoundaryWithLogger>
      {/* <div className="noPLP" /> */}
    </div>
  );
}
